import { type ReactNode, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ZodError } from 'zod';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useAuth } from '@/modules/auth/presentation/context/AuthContext';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { useAccessRolesQuery } from '@/modules/profiles/application/profileQueries';
import { type AccessRole } from '@/modules/profiles/domain/entities/AccessRole';
import { BackButton } from '@/presentation/components/BackButton';
import { db } from '@/shared/db/database';
import { useQualitySettings, DEFAULT_QUALITY_SETTINGS, applyQualityPreset } from '@/shared/hooks/useQualitySettings';
import type { LivenessMode } from '@/shared/config/quality.config';
import { debugTelemetryService } from '@/shared/services/debugTelemetryService';
import { FieldError } from '@/shared/ui/form/FieldError';
import { mapZodErrorsToFieldErrors } from '@/shared/utils/zodErrors';

/**
 * Split a `key|min=X|max=Y` marker from mapZodErrorsToFieldErrors and resolve
 * via i18n. When `raw` is a plain key (no pipe), calls `t(raw)` directly.
 */
function resolveFieldError(t: TFunction, raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const [key, ...paramParts] = raw.split('|');
  if (!key) return undefined;
  const params: Record<string, string> = {};
  for (const part of paramParts) {
    const eq = part.indexOf('=');
    if (eq > 0) params[part.slice(0, eq)] = part.slice(eq + 1);
  }
  return t(key, params);
}

type ModalKey = 'idioma' | 'perfil' | 'params' | 'acerca' | null;

// External web apps the admin can launch from the kiosk config screen.
// Plain external links — no SDK, no auth handoff; the destination handles its own login.
const ADMIN_MODE_URL = 'https://admin.myintelli.net/';
const EMPLOYEE_MODE_URL = 'https://app.myintelli.net/';

function clamp(val: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, isNaN(val) ? min : val));
}

const PARAMS_KEY = 'mi_terminal_params';

function loadParams(): { debugger: boolean; threshold: number } {
  try {
    const raw = localStorage.getItem(PARAMS_KEY);
    return raw
      ? (JSON.parse(raw) as { debugger: boolean; threshold: number })
      : { debugger: false, threshold: 1.2 };
  } catch {
    return { debugger: false, threshold: 1.2 };
  }
}

function saveParams(p: { debugger: boolean; threshold: number }): void {
  localStorage.setItem(PARAMS_KEY, JSON.stringify(p));
}

function Toggle({ on, onChange }: { on: boolean; onChange: () => void }) {
  const { C, T } = useTheme();
  return (
    <div
      onClick={onChange}
      style={{ width: 34, height: 18, borderRadius: 10, background: on ? C.blue : T.border, position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0 }}
    >
      <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: on ? 18 : 2, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.15)' }} />
    </div>
  );
}

function ModalShell({ title, onClose, children, hideFooter, onAccept }: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  hideFooter?: boolean;
  onAccept?: () => void;
}) {
  const { C, T } = useTheme();
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: T.modalBg, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={(e) => { e.stopPropagation(); }}
        style={{ background: T.surfaceBg, borderRadius: 16, padding: '18px 24px 24px', width: '100%', maxWidth: 440, boxShadow: T.shadow, maxHeight: '85vh', display: 'flex', flexDirection: 'column', position: 'relative' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, position: 'relative', minHeight: 28 }}>
          <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 13, fontWeight: 700, color: T.clockText, pointerEvents: 'none' }}>{title}</div>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: `${C.danger}12`, color: C.danger, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .15s', position: 'relative', zIndex: 1 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${C.danger}22`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = `${C.danger}12`; }}
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, overflowY: 'auto', flex: '1 1 auto' }}>{children}</div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '11px', borderRadius: 9, border: 'none', background: C.danger, color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', boxShadow: `0 4px 12px ${C.danger}33` }}
          >
            Cerrar
          </button>
          {!hideFooter && (
            <button
              onClick={onAccept}
              style={{ flex: 1, padding: '11px', borderRadius: 9, border: 'none', background: C.success, color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', boxShadow: `0 4px 12px ${C.success}33` }}
            >
              Aceptar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminConfigPage() {
  const { C, T, theme, toggle } = useTheme();
  const navigate = useNavigate();
  const auth = useAuth();
  const { t, i18n } = useTranslation('common');
  const { selectedRole, selectRole } = useProfile();
  const { data: accessRoles = [] } = useAccessRolesQuery();

  const [openModal, setOpenModal] = useState<ModalKey>(null);

  // idioma
  const [idiomaVal, setIdiomaVal] = useState<string>(
    i18n.language === 'en' ? 'Inglés' : 'Español',
  );

  // perfil
  const [perfilSearch, setPerfilSearch] = useState('');
  const [perfilVal, setPerfilVal] = useState<AccessRole | null>(selectedRole);

  // params
  const [debuggerVal, setDebuggerVal] = useState(() => loadParams().debugger);
  const [thresholdVal, setThresholdVal] = useState(() => loadParams().threshold);
  const [debugTelemetryVal, setDebugTelemetryVal] = useState(() => debugTelemetryService.getSync());

  // quality settings
  const {
    thresholds,
    livenessMode: livMode,
    qualityGateTimeoutMs: qlGateMs,
    matchThreshold: mThreshold,
    passiveHigh,
    passiveLow,
    livenessWindowSize,
    livenessLiveCount,
    livenessSpoofConsecutive,
    livenessMaxTotalFrames,
    blurMinLaplacianVar,
    brightnessMinGateB,
    brightnessMaxGateB,
    faceMinShortSidePx,
    homeButtonAuto,
    consecutiveTimelogMinSeconds,
    qualityPreset,
    frameThrottleFps,
    save: qsSave,
  } = useQualitySettings();

  // quality modal form state (initialized when modal opens)
  const [qs_blur, setQsBlur] = useState(DEFAULT_QUALITY_SETTINGS.thresholds.blurThreshold);
  const [qs_brightnessMin, setQsBrightnessMin] = useState(DEFAULT_QUALITY_SETTINGS.thresholds.brightnessMin);
  const [qs_brightnessMax, setQsBrightnessMax] = useState(DEFAULT_QUALITY_SETTINGS.thresholds.brightnessMax);
  const [qs_faceMinPx, setQsFaceMinPx] = useState(DEFAULT_QUALITY_SETTINGS.thresholds.faceMinPx);
  const [qs_yaw, setQsYaw] = useState(DEFAULT_QUALITY_SETTINGS.thresholds.yawThresholdDeg);
  const [qs_pitch, setQsPitch] = useState(DEFAULT_QUALITY_SETTINGS.thresholds.pitchThresholdDeg);
  const [qs_livenessTimeout, setQsLivenessTimeout] = useState(DEFAULT_QUALITY_SETTINGS.thresholds.livenessDirectionTimeoutMs);
  const [qs_qualityGateTimeout, setQsQualityGateTimeout] = useState(DEFAULT_QUALITY_SETTINGS.qualityGateTimeoutMs);
  const [qs_livenessMode, setQsLivenessMode] = useState<LivenessMode>(DEFAULT_QUALITY_SETTINGS.livenessMode);
  const [qs_homeButtonAuto, setQsHomeButtonAuto] = useState<boolean>(DEFAULT_QUALITY_SETTINGS.homeButtonAuto);
  const [qs_qualityPreset, setQsQualityPreset] = useState<'default' | 'high-contrast'>(DEFAULT_QUALITY_SETTINGS.qualityPreset);
  const [qs_frameThrottleFps, setQsFrameThrottleFps] = useState(DEFAULT_QUALITY_SETTINGS.frameThrottleFps);
  const [qs_matchThreshold, setQsMatchThreshold] = useState(DEFAULT_QUALITY_SETTINGS.matchThreshold);
  // NEW Slice 3: Liveness Aggregator state
  const [qs_passiveHigh, setQsPassiveHigh] = useState(DEFAULT_QUALITY_SETTINGS.passiveHigh);
  const [qs_passiveLow, setQsPassiveLow] = useState(DEFAULT_QUALITY_SETTINGS.passiveLow);
  const [qs_livenessWindowSize, setQsLivenessWindowSize] = useState(DEFAULT_QUALITY_SETTINGS.livenessWindowSize);
  const [qs_livenessLiveCount, setQsLivenessLiveCount] = useState(DEFAULT_QUALITY_SETTINGS.livenessLiveCount);
  const [qs_livenessSpoofConsecutive, setQsLivenessSpoofConsecutive] = useState(DEFAULT_QUALITY_SETTINGS.livenessSpoofConsecutive);
  const [qs_livenessMaxTotalFrames, setQsLivenessMaxTotalFrames] = useState(DEFAULT_QUALITY_SETTINGS.livenessMaxTotalFrames);
  // NEW Slice 3: Gate B override state
  const [qs_blurMinLaplacianVar, setQsBlurMinLaplacianVar] = useState(DEFAULT_QUALITY_SETTINGS.blurMinLaplacianVar);
  const [qs_brightnessMinGateB, setQsBrightnessMinGateB] = useState(DEFAULT_QUALITY_SETTINGS.brightnessMinGateB);
  const [qs_brightnessMaxGateB, setQsBrightnessMaxGateB] = useState(DEFAULT_QUALITY_SETTINGS.brightnessMaxGateB);
  const [qs_faceMinShortSidePx, setQsFaceMinShortSidePx] = useState(DEFAULT_QUALITY_SETTINGS.faceMinShortSidePx);
  const [qs_consecutiveTimelogMinSeconds, setQsConsecutiveTimelogMinSeconds] = useState(DEFAULT_QUALITY_SETTINGS.consecutiveTimelogMinSeconds);
  const [qs_saving, setQsSaving] = useState(false);
  // Per-field error map. Keys are joined Zod paths (e.g. 'livenessLiveCount',
  // 'thresholds.blurThreshold') or the reserved '__generic' for non-Zod errors.
  // Values are i18n keys (optionally suffixed with `|min=X|max=Y` markers).
  const [qsFieldErrors, setQsFieldErrors] = useState<Record<string, string>>({});

  // about data
  const [employeeCount, setEmployeeCount] = useState<number | null>(null);
  const [timelogCount, setTimelogCount] = useState<number | null>(null);
  const [storageInfo, setStorageInfo] = useState<string | null>(null);
  const [deviceSerial, setDeviceSerial] = useState<string>('—');
  const [deviceModel, setDeviceModel] = useState<string>('—');

  useEffect(() => {
    void db.employees.count().then(setEmployeeCount);
    void db.timelogs.count().then(setTimelogCount);
    if (navigator.storage?.estimate) {
      void navigator.storage.estimate().then((est) => {
        const mb = ((est.usage ?? 0) / 1024 / 1024).toFixed(2);
        setStorageInfo(`${mb} MB`);
      });
    }

    // Load device info from backend enrollment (real data only, no mock)
    void (async () => {
      // Serial: try localStorage → Dexie → no data
      let serial = localStorage.getItem('mi_enrolled_device_serial');
      if (!serial) {
        try {
          const dbSerial = await db.settings.get('mi_enrolled_device_serial');
          serial = dbSerial?.value ?? null;
        } catch { /* ignore */ }
      }
      setDeviceSerial(serial ?? '—');

      // Model: try localStorage → Dexie → no data
      let model = localStorage.getItem('mi_enrolled_device_model');
      if (!model) {
        try {
          const dbModel = await db.settings.get('mi_enrolled_device_model');
          model = dbModel?.value ?? null;
        } catch { /* ignore */ }
      }
      setDeviceModel(model ?? '—');
    })();
  }, []);

  const isDark = theme === 'dark';
  const currentParams = loadParams();
  const paramsDisplay = `Debugger ${currentParams.debugger ? 'activo' : 'inactivo'} · Threshold ${currentParams.threshold}`;

  const perfilDisplay = (() => {
    const cur = selectedRole;
    return cur ? `${cur.name} · ${cur.typeLabel}` : 'Seleccionar perfil';
  })();

  const initQualityForm = useCallback(() => {
    setQsBlur(thresholds.blurThreshold);
    setQsBrightnessMin(thresholds.brightnessMin);
    setQsBrightnessMax(thresholds.brightnessMax);
    setQsFaceMinPx(thresholds.faceMinPx);
    setQsYaw(thresholds.yawThresholdDeg);
    setQsPitch(thresholds.pitchThresholdDeg);
    setQsLivenessTimeout(thresholds.livenessDirectionTimeoutMs);
    setQsQualityGateTimeout(qlGateMs);
    setQsLivenessMode(livMode);
    setQsMatchThreshold(mThreshold);
    // NEW Slice 3
    setQsPassiveHigh(passiveHigh);
    setQsPassiveLow(passiveLow);
    setQsLivenessWindowSize(livenessWindowSize);
    setQsLivenessLiveCount(livenessLiveCount);
    setQsLivenessSpoofConsecutive(livenessSpoofConsecutive);
    setQsLivenessMaxTotalFrames(livenessMaxTotalFrames);
    setQsBlurMinLaplacianVar(blurMinLaplacianVar);
    setQsBrightnessMinGateB(brightnessMinGateB);
    setQsBrightnessMaxGateB(brightnessMaxGateB);
    setQsFaceMinShortSidePx(faceMinShortSidePx);
    setQsHomeButtonAuto(homeButtonAuto);
    setQsConsecutiveTimelogMinSeconds(consecutiveTimelogMinSeconds);
    setQsQualityPreset(qualityPreset);
    setQsFrameThrottleFps(frameThrottleFps);
  }, [thresholds, livMode, qlGateMs, mThreshold, passiveHigh, passiveLow, livenessWindowSize, livenessLiveCount, livenessSpoofConsecutive, livenessMaxTotalFrames, blurMinLaplacianVar, brightnessMinGateB, brightnessMaxGateB, faceMinShortSidePx, homeButtonAuto, consecutiveTimelogMinSeconds, qualityPreset, frameThrottleFps]);

  const handleQsSave = useCallback(async () => {
    setQsSaving(true);
    setQsFieldErrors({});
    try {
      await qsSave({
        thresholds: {
          blurThreshold: qs_blur,
          brightnessMin: qs_brightnessMin,
          brightnessMax: qs_brightnessMax,
          faceMinPx: qs_faceMinPx,
          yawThresholdDeg: qs_yaw,
          pitchThresholdDeg: qs_pitch,
          livenessDirectionTimeoutMs: qs_livenessTimeout,
        },
        livenessMode: qs_livenessMode,
        qualityGateTimeoutMs: qs_qualityGateTimeout,
        matchThreshold: qs_matchThreshold,
        // NEW Slice 3
        passiveHigh: qs_passiveHigh,
        passiveLow: qs_passiveLow,
        livenessWindowSize: qs_livenessWindowSize,
        livenessLiveCount: qs_livenessLiveCount,
        livenessSpoofConsecutive: qs_livenessSpoofConsecutive,
        livenessMaxTotalFrames: qs_livenessMaxTotalFrames,
        blurMinLaplacianVar: qs_blurMinLaplacianVar,
        brightnessMinGateB: qs_brightnessMinGateB,
        brightnessMaxGateB: qs_brightnessMaxGateB,
        faceMinShortSidePx: qs_faceMinShortSidePx,
        homeButtonAuto: qs_homeButtonAuto,
        consecutiveTimelogMinSeconds: qs_consecutiveTimelogMinSeconds,
        qualityPreset: qs_qualityPreset,
        frameThrottleFps: qs_frameThrottleFps,
      });
      // Happy path — reset any lingering errors and close modal.
      setQsFieldErrors({});
      setOpenModal(null);
    } catch (err) {
      if (err instanceof ZodError) {
        setQsFieldErrors(mapZodErrorsToFieldErrors(err));
      } else {
        setQsFieldErrors({ __generic: 'admin.qualityErrors.unexpected' });
      }
    } finally {
      setQsSaving(false);
    }
  }, [qsSave, qs_blur, qs_brightnessMin, qs_brightnessMax, qs_faceMinPx, qs_yaw, qs_pitch, qs_livenessTimeout, qs_livenessMode, qs_qualityGateTimeout, qs_matchThreshold, qs_passiveHigh, qs_passiveLow, qs_livenessWindowSize, qs_livenessLiveCount, qs_livenessSpoofConsecutive, qs_livenessMaxTotalFrames, qs_blurMinLaplacianVar, qs_brightnessMinGateB, qs_brightnessMaxGateB, qs_faceMinShortSidePx, qs_homeButtonAuto, qs_consecutiveTimelogMinSeconds, qs_qualityPreset]);

  const clearFieldError = useCallback((path: string) => {
    setQsFieldErrors((prev) => {
      if (!prev[path]) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }, []);

  // Reset loads defaults into the form WITHOUT persisting — user must still click Guardar
  const handleQsReset = useCallback(() => {
    const d = DEFAULT_QUALITY_SETTINGS;
    setQsBlur(d.thresholds.blurThreshold);
    setQsBrightnessMin(d.thresholds.brightnessMin);
    setQsBrightnessMax(d.thresholds.brightnessMax);
    setQsFaceMinPx(d.thresholds.faceMinPx);
    setQsYaw(d.thresholds.yawThresholdDeg);
    setQsPitch(d.thresholds.pitchThresholdDeg);
    setQsLivenessTimeout(d.thresholds.livenessDirectionTimeoutMs);
    setQsQualityGateTimeout(d.qualityGateTimeoutMs);
    setQsLivenessMode(d.livenessMode);
    setQsMatchThreshold(d.matchThreshold);
    // NEW Slice 3
    setQsPassiveHigh(d.passiveHigh);
    setQsPassiveLow(d.passiveLow);
    setQsLivenessWindowSize(d.livenessWindowSize);
    setQsLivenessLiveCount(d.livenessLiveCount);
    setQsLivenessSpoofConsecutive(d.livenessSpoofConsecutive);
    setQsLivenessMaxTotalFrames(d.livenessMaxTotalFrames);
    setQsBlurMinLaplacianVar(d.blurMinLaplacianVar);
    setQsBrightnessMinGateB(d.brightnessMinGateB);
    setQsBrightnessMaxGateB(d.brightnessMaxGateB);
    setQsFaceMinShortSidePx(d.faceMinShortSidePx);
    setQsHomeButtonAuto(d.homeButtonAuto);
    setQsConsecutiveTimelogMinSeconds(d.consecutiveTimelogMinSeconds);
    setQsQualityPreset(d.qualityPreset);
    setQsFrameThrottleFps(d.frameThrottleFps);
    // Do NOT call qsReset() or close modal — user reviews and confirms with Guardar
  }, []);

  // Apply quality preset (default or high-contrast)
  // Only affects image quality, matching, and fraud detection — preserves liveness mode and home button
  const handleApplyPreset = useCallback((preset: 'default' | 'high-contrast') => {
    const currentSettings = {
      thresholds: { blurThreshold: qs_blur, brightnessMin: qs_brightnessMin, brightnessMax: qs_brightnessMax, faceMinPx: qs_faceMinPx, yawThresholdDeg: qs_yaw, pitchThresholdDeg: qs_pitch, livenessDirectionTimeoutMs: qs_livenessTimeout },
      livenessMode: qs_livenessMode,
      qualityGateTimeoutMs: qs_qualityGateTimeout,
      matchThreshold: qs_matchThreshold,
      passiveHigh: qs_passiveHigh,
      passiveLow: qs_passiveLow,
      livenessWindowSize: qs_livenessWindowSize,
      livenessLiveCount: qs_livenessLiveCount,
      livenessSpoofConsecutive: qs_livenessSpoofConsecutive,
      livenessMaxTotalFrames: qs_livenessMaxTotalFrames,
      blurMinLaplacianVar: qs_blurMinLaplacianVar,
      brightnessMinGateB: qs_brightnessMinGateB,
      brightnessMaxGateB: qs_brightnessMaxGateB,
      faceMinShortSidePx: qs_faceMinShortSidePx,
      homeButtonAuto: qs_homeButtonAuto,
      consecutiveTimelogMinSeconds: qs_consecutiveTimelogMinSeconds,
      qualityPreset: qs_qualityPreset,
      frameThrottleFps: qs_frameThrottleFps,
    };

    const applied = applyQualityPreset(preset, currentSettings);

    // Update form state with preset values
    setQsBlur(applied.thresholds.blurThreshold);
    setQsBrightnessMin(applied.thresholds.brightnessMin);
    setQsBrightnessMax(applied.thresholds.brightnessMax);
    setQsFaceMinPx(applied.thresholds.faceMinPx);
    setQsMatchThreshold(applied.matchThreshold);
    setQsPassiveHigh(applied.passiveHigh);
    setQsPassiveLow(applied.passiveLow);
    setQsBlurMinLaplacianVar(applied.blurMinLaplacianVar);
    setQsBrightnessMinGateB(applied.brightnessMinGateB);
    setQsBrightnessMaxGateB(applied.brightnessMaxGateB);
    setQsFaceMinShortSidePx(applied.faceMinShortSidePx);
    setQsFrameThrottleFps(applied.frameThrottleFps);
    setQsQualityPreset(preset);
  }, [qs_blur, qs_brightnessMin, qs_brightnessMax, qs_faceMinPx, qs_yaw, qs_pitch, qs_livenessTimeout, qs_livenessMode, qs_qualityGateTimeout, qs_matchThreshold, qs_passiveHigh, qs_passiveLow, qs_livenessWindowSize, qs_livenessLiveCount, qs_livenessSpoofConsecutive, qs_livenessMaxTotalFrames, qs_blurMinLaplacianVar, qs_brightnessMinGateB, qs_brightnessMaxGateB, qs_faceMinShortSidePx, qs_homeButtonAuto, qs_consecutiveTimelogMinSeconds, qs_qualityPreset, qs_frameThrottleFps]);

  const handleLogout = () => {
    auth.logout();
    void navigate('/', { replace: true });
  };

  const handleItemClick = (k: NonNullable<ModalKey> | 'tema', action: 'modal' | 'toggleTheme') => {
    if (action === 'toggleTheme') {
      toggle();
    } else {
      if (k === 'perfil') { setPerfilVal(selectedRole); setPerfilSearch(''); }
      if (k === 'params') { const p = loadParams(); setDebuggerVal(p.debugger); setThresholdVal(p.threshold); initQualityForm(); setDebugTelemetryVal(debugTelemetryService.getSync()); setQsFieldErrors({}); }
      if (k === 'idioma') { setIdiomaVal(i18n.language === 'en' ? 'Inglés' : 'Español'); }
      setOpenModal(k as NonNullable<ModalKey>);
    }
  };

  const filteredRoles = accessRoles.filter((r) => {
    const q = perfilSearch.trim().toLowerCase();
    if (!q) return true;
    return r.name.toLowerCase().includes(q) || r.typeLabel.toLowerCase().includes(q);
  });

  // Combined display for params item: shows debugger + quality summary
  void livMode; void mThreshold; // used in modal form state, not in display

  const ITEMS = [
    {
      k: 'idioma' as const,
      label: t('admin.configLanguage'),
      val: idiomaVal,
      action: 'modal' as const,
      icon: (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 9h4" /><path d="M9 9v4" /><path d="M7 13h4" />
          <path d="M14 15l2-6 2 6" /><path d="M14.5 13.5h3" />
        </svg>
      ),
    },
    {
      k: 'perfil' as const,
      label: t('admin.configPerfil'),
      val: perfilDisplay,
      action: 'modal' as const,
      icon: (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 0 1 14-7.5" /><polyline points="17 2 17 5 20 5" />
          <path d="M21 12a9 9 0 0 1-14 7.5" /><polyline points="7 22 7 19 4 19" />
        </svg>
      ),
    },
    {
      k: 'params' as const,
      label: t('admin.configParams'),
      val: paramsDisplay,
      action: 'modal' as const,
      icon: (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      ),
    },
    {
      k: 'acerca' as const,
      label: t('admin.configAbout'),
      val: `Versión 1.0.55 · Instancia ${auth.session?.idInstance ?? '—'}`,
      action: 'modal' as const,
      icon: (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      ),
    },
    {
      k: 'tema' as const,
      label: isDark ? t('admin.configThemeDark') : t('admin.configThemeLight'),
      val: t('admin.configThemeHint'),
      action: 'toggleTheme' as const,
      icon: isDark ? (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="22" />
          <line x1="2" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="22" y2="12" />
          <line x1="4.93" y1="4.93" x2="7.05" y2="7.05" /><line x1="16.95" y1="16.95" x2="19.07" y2="19.07" />
          <line x1="4.93" y1="19.07" x2="7.05" y2="16.95" /><line x1="16.95" y1="7.05" x2="19.07" y2="4.93" />
        </svg>
      ) : (
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      ),
    },
  ] as const;

  const ABOUT_ROWS = [
    {
      label: t('admin.aboutVersion'),
      val: '1.0.55',
      icon: <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="4" y="3" width="16" height="18" rx="2" /><polyline points="9 8 12 11 15 8" /></svg>,
    },
    {
      label: t('admin.aboutInstance'),
      val: auth.session?.idInstance?.toString() ?? '—',
      icon: <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="6" cy="6" r="2" /><circle cx="6" cy="18" r="2" /><circle cx="18" cy="12" r="2" /><path d="M6 8v8" /><path d="M6 12h9" /></svg>,
    },
    {
      label: t('admin.aboutModel'),
      val: deviceModel,
      icon: <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>,
    },
    {
      label: t('admin.aboutSerial'),
      val: deviceSerial,
      mono: true,
      icon: <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>,
    },
    {
      label: t('admin.aboutTimelogs'),
      val: timelogCount !== null ? timelogCount.toString() : '…',
      icon: <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>,
    },
    {
      label: t('admin.aboutEmployees'),
      val: employeeCount !== null ? employeeCount.toString() : '…',
      icon: <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
    },
    {
      label: t('admin.aboutStorage'),
      val: storageInfo ?? '…',
      icon: <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
    },
  ];

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '24px 30px 18px', borderBottom: `1px solid ${T.border}`, background: T.surfaceBg, flexShrink: 0 }}>
        <div style={{ maxWidth: 645, margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}>
          <BackButton T={T} onClick={() => { void navigate('/admin'); }} />
          <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.text, pointerEvents: 'none' }}>
            {t('admin.configTitle')}
          </div>
        </div>
      </div>

      {/* Items */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 30px 30px' }}>
        <div style={{ maxWidth: 645, margin: '0 auto' }}>
          {ITEMS.map((it) => (
            <div
              key={it.k}
              data-testid={it.k === 'params' ? 'params-config-item' : undefined}
              onClick={() => { handleItemClick(it.k, it.action); }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', borderRadius: 11, cursor: 'pointer', background: T.surfaceBg, border: `1px solid ${T.border}`, marginBottom: 7, transition: 'all .15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.rowBg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = T.surfaceBg; }}
            >
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `${C.blue}12`, color: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {it.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{it.label}</div>
                <div style={{ fontSize: 9, color: T.textMuted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.val}</div>
              </div>
              {it.action === 'toggleTheme' ? (
                <Toggle on={isDark} onChange={toggle} />
              ) : (
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </div>
          ))}

          {/* Mode shortcuts — quick links to the Admin/Employee web apps in a new tab.
              Rendered above the logout button per design §5 of resto-implicaciones-esteticas-pase-produccion. */}
          <div style={{ marginTop: 14, display: 'flex', gap: 10, marginBottom: 10 }}>
            <a
              href={ADMIN_MODE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: 1, padding: '12px', borderRadius: 11, background: '#8FDDB1', color: '#1B6B3F', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}
            >
              {t('admin.configModeAdmin')}
            </a>
            <a
              href={EMPLOYEE_MODE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flex: 1, padding: '12px', borderRadius: 11, background: '#9DE6E2', color: '#0F6D69', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}
            >
              {t('admin.configModeEmployee')}
            </a>
          </div>

          <button
            onClick={handleLogout}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 11, background: `${C.danger}10`, border: `1px solid ${C.danger}33`, color: C.danger, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700 }}
          >
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t('admin.configSignOut')}
          </button>
        </div>
      </div>

      {/* Modal — Idioma */}
      {openModal === 'idioma' && (
        <ModalShell
          title={t('admin.languageModalTitle')}
          onClose={() => { setOpenModal(null); }}
          onAccept={() => {
            void i18n.changeLanguage(idiomaVal === 'Inglés' ? 'en' : 'es');
            setOpenModal(null);
          }}
        >
          {[{ k: 'Español', flag: '🇪🇸' }, { k: 'Inglés', flag: '🇺🇸' }].map((o) => {
            const sel = idiomaVal === o.k;
            return (
              <button
                key={o.k}
                onClick={() => { setIdiomaVal(o.k); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, border: `1px solid ${sel ? C.blue : T.border}`, background: sel ? `${C.blue}10` : T.surfaceBg, fontFamily: 'inherit', color: T.text, fontSize: 11, fontWeight: sel ? 700 : 500, cursor: 'pointer', textAlign: 'left', transition: 'all .15s' }}
              >
                <span style={{ fontSize: 16 }}>{o.flag}</span>
                <span style={{ flex: 1 }}>{o.k === 'Español' ? t('admin.languageSpanish') : t('admin.languageEnglish')}</span>
                <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${sel ? C.blue : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {sel && <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.blue }} />}
                </div>
              </button>
            );
          })}
        </ModalShell>
      )}

      {/* Modal — Perfil de Clock */}
      {openModal === 'perfil' && (
        <ModalShell
          title={t('admin.perfilModalTitle')}
          onClose={() => { setOpenModal(null); }}
          onAccept={() => {
            if (perfilVal) selectRole(perfilVal);
            setOpenModal(null);
          }}
        >
          <div style={{ position: 'relative', marginBottom: 4 }}>
            <svg
              width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round"
              style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={perfilSearch}
              onChange={(e) => { setPerfilSearch(e.target.value); }}
              placeholder={t('admin.perfilSearchPlaceholder')}
              style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.rowBg, color: T.text, fontFamily: 'inherit', fontSize: 11, outline: 'none', boxSizing: 'border-box' }}
              onFocus={(e) => { e.target.style.borderColor = C.blue; }}
              onBlur={(e) => { e.target.style.borderColor = T.border; }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto', paddingRight: 2 }}>
            {filteredRoles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: T.textMuted, fontSize: 10 }}>{t('admin.perfilNoResults')}</div>
            ) : (
              filteredRoles.map((r) => {
                const sel = perfilVal?.idAccessRole === r.idAccessRole;
                return (
                  <button
                    key={r.idAccessRole}
                    onClick={() => { setPerfilVal(r); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 10, border: `1px solid ${sel ? C.blue : T.border}`, background: sel ? `${C.blue}10` : T.surfaceBg, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', transition: 'all .15s' }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: sel ? C.blue : `${C.blue}15`, color: sel ? '#fff' : C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{r.name}</div>
                      <div style={{ fontSize: 9, color: T.textMuted, marginTop: 2 }}>{r.typeLabel} · {r.employeeCount.toLocaleString('es')} empleados</div>
                    </div>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${sel ? C.blue : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {sel && <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.blue }} />}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </ModalShell>
      )}

      {/* Modal — Parámetros */}
      {openModal === 'params' && (
        <div
          onClick={() => { setOpenModal(null); }}
          style={{ position: 'fixed', inset: 0, background: T.modalBg, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div
            onClick={(e) => { e.stopPropagation(); }}
            style={{ background: T.surfaceBg, borderRadius: 16, padding: '18px 28px 24px', width: '100%', maxWidth: 820, boxShadow: T.shadow, maxHeight: '92vh', display: 'flex', flexDirection: 'column', position: 'relative' }}
          >
            {/* Header — larger + face-icon chip next to the title, red X on
                a solid blue circle (matches the design mock 2026-07-18). */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, position: 'relative', minHeight: 32 }}>
              <div style={{ position: 'absolute', left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${C.blue}18`, color: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                    <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                    <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                    <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                    <circle cx="12" cy="11" r="3" />
                    <path d="M7 17c1-2 3-3 5-3s4 1 5 3" />
                  </svg>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.clockText }}>{t('admin.paramsModalTitle')}</div>
              </div>
              <div style={{ flex: 1 }} />
              <button onClick={() => { setOpenModal(null); }} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: C.blue, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            {/* Scrollable content — minimal iOS-style scrollbar */}
            <div data-testid="qs-modal" style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', flex: '1 1 auto', marginBottom: 14, scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.25) transparent' }}
              className="mi-thin-scroll"
            >
              {/* Generic error notice — surfaces non-Zod save failures at the top so
                  keyboard-focused users don't lose context after clicking Save. */}
              {qsFieldErrors.__generic && (
                <div style={{ marginBottom: 8 }}>
                  <FieldError testId="field-error-generic" error={resolveFieldError(t, qsFieldErrors.__generic)} />
                </div>
              )}

              {/* Section — General */}
              <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4, marginTop: 4 }}>{t('admin.qualityModalSectionGeneral')}</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 2px', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{t('admin.paramsDebugger')}</div>
                <div
                  onClick={() => { setDebuggerVal((v) => !v); }}
                  style={{ width: 40, height: 22, borderRadius: 12, background: debuggerVal ? C.blue : T.border, position: 'relative', cursor: 'pointer', transition: 'background .2s' }}
                >
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: debuggerVal ? 20 : 2, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                </div>
              </div>

              {/* Debug Telemetry toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 2px', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{t('admin.qualityModalDebugTelemetry')}</div>
                  <div style={{ fontSize: 9, color: T.textMuted, marginTop: 1 }}>{t('admin.qualityModalDebugTelemetryHelp')}</div>
                </div>
                <div
                  data-testid="toggle-debug-telemetry"
                  onClick={() => {
                    const next = !debugTelemetryVal;
                    setDebugTelemetryVal(next);
                    void debugTelemetryService.save(next);
                  }}
                  style={{ width: 40, height: 22, borderRadius: 12, background: debugTelemetryVal ? C.blue : T.border, position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0 }}
                >
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: debugTelemetryVal ? 20 : 2, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                </div>
              </div>

              <div style={{ fontSize: 10, fontWeight: 800, color: T.text, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, marginTop: 12 }}>Modo home</div>
              <div style={{ padding: '7px 2px' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([
                    { auto: true,  testId: 'qs-home-mode-auto',   label: 'Automático', desc: 'El sistema decide entrada o salida' },
                    { auto: false, testId: 'qs-home-mode-manual', label: 'Manual',     desc: 'Elegís entrada o salida' },
                  ] as const).map((opt) => {
                    const sel = qs_homeButtonAuto === opt.auto;
                    return (
                      <button key={opt.testId} data-testid={opt.testId} onClick={() => { setQsHomeButtonAuto(opt.auto); }}
                        style={{ flex: 1, padding: '8px 6px', borderRadius: 9, border: `1px solid ${sel ? C.blue : T.border}`, background: sel ? `${C.blue}10` : T.surfaceBg, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'center' }}
                      >
                        <div style={{ fontSize: 10, fontWeight: 700, color: sel ? C.blue : T.text }}>{opt.label}</div>
                        <div style={{ fontSize: 8, color: T.textMuted, marginTop: 2 }}>{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section — Quality Preset (after Modo home) */}
              <div style={{ fontSize: 10, fontWeight: 800, color: T.text, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, marginTop: 12 }}>{t('admin.qualityModalPresetLabel')}</div>
              <div style={{ padding: '7px 2px' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([
                    { preset: 'default' as const, testId: 'qs-preset-default', labelKey: 'admin.qualityModalPresetDefault', descKey: 'admin.qualityModalPresetDefaultDesc' },
                    { preset: 'high-contrast' as const, testId: 'qs-preset-high-contrast', labelKey: 'admin.qualityModalPresetHighContrast', descKey: 'admin.qualityModalPresetHighContrastDesc' },
                  ] as const).map((opt) => {
                    const sel = qs_qualityPreset === opt.preset;
                    return (
                      <button key={opt.preset} data-testid={opt.testId} onClick={() => { handleApplyPreset(opt.preset); }}
                        style={{ flex: 1, padding: '8px 6px', borderRadius: 9, border: `1px solid ${sel ? C.blue : T.border}`, background: sel ? `${C.blue}10` : T.surfaceBg, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'center' }}
                      >
                        <div style={{ fontSize: 10, fontWeight: 700, color: sel ? C.blue : T.text }}>{t(opt.labelKey)}</div>
                        <div style={{ fontSize: 8, color: T.textMuted, marginTop: 2 }}>{t(opt.descKey)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section — Modo liveness (moved above Calidad per B.2) */}
              <div style={{ fontSize: 10, fontWeight: 800, color: T.text, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, marginTop: 12 }}>{t('admin.qualityModalSectionLiveness')}</div>
              <div style={{ padding: '7px 2px' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {([
                    { mode: 'disabled' as const, testId: 'qs-liveness-disabled', labelKey: 'admin.qualityModalLivenessDisabled', descKey: 'admin.qualityModalLivenessDisabledDesc' },
                    { mode: 'minimum' as const, testId: 'qs-liveness-minimum', labelKey: 'admin.qualityModalLivenessMinimum', descKey: 'admin.qualityModalLivenessMinimumDesc' },
                    { mode: 'strong' as const, testId: 'qs-liveness-strong', labelKey: 'admin.qualityModalLivenessStrong', descKey: 'admin.qualityModalLivenessStrongDesc' },
                  ] as const).map((opt) => {
                    const sel = qs_livenessMode === opt.mode;
                    return (
                      <button key={opt.mode} data-testid={opt.testId} onClick={() => { setQsLivenessMode(opt.mode); }}
                        style={{ flex: 1, padding: '8px 6px', borderRadius: 9, border: `1px solid ${sel ? C.blue : T.border}`, background: sel ? `${C.blue}10` : T.surfaceBg, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'center' }}
                      >
                        <div style={{ fontSize: 10, fontWeight: 700, color: sel ? C.blue : T.text }}>{t(opt.labelKey)}</div>
                        <div style={{ fontSize: 8, color: T.textMuted, marginTop: 2 }}>{t(opt.descKey)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section — Calidad de imagen (8 options in a 2-column COLUMN-MAJOR grid:
                  left col = nitidez, brillo min, brillo max, tamaño min; right col = giro h, inclinación v, tiempo por dirección, tiempo total quality gate) */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 800, color: T.text, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, marginTop: 12 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                {t('admin.qualityModalSectionQuality')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(4, auto)', gridAutoFlow: 'column', columnGap: 16, rowGap: 0 }}>
                {([
                  { labelKey: 'admin.qualityModalBlurLabel', id: 'qs-input-blur', val: qs_blur, set: setQsBlur, min: 0, max: 800, helpKey: 'admin.qualityModalBlurHelp' },
                  { labelKey: 'admin.qualityModalBrightnessMinLabel', id: 'qs-input-brightnessMin', val: qs_brightnessMin, set: setQsBrightnessMin, min: 20, max: 180, helpKey: 'admin.qualityModalBrightnessMinHelp' },
                  { labelKey: 'admin.qualityModalBrightnessMaxLabel', id: 'qs-input-brightnessMax', val: qs_brightnessMax, set: setQsBrightnessMax, min: 100, max: 254, helpKey: 'admin.qualityModalBrightnessMaxHelp' },
                  { labelKey: 'admin.qualityModalFaceMinPxLabel', id: 'qs-input-faceMinPx', val: qs_faceMinPx, set: setQsFaceMinPx, min: 40, max: 300, helpKey: 'admin.qualityModalFaceMinPxHelp' },
                  { labelKey: 'admin.qualityModalYawLabel', id: 'qs-input-yaw', val: qs_yaw, set: setQsYaw, min: 10, max: 45, helpKey: 'admin.qualityModalYawHelp' },
                  { labelKey: 'admin.qualityModalPitchLabel', id: 'qs-input-pitch', val: qs_pitch, set: setQsPitch, min: 10, max: 45, helpKey: 'admin.qualityModalPitchHelp' },
                  { labelKey: 'admin.qualityModalLivenessTimeoutLabel', id: 'qs-input-livenessTimeout', val: qs_livenessTimeout, set: setQsLivenessTimeout, min: 1000, max: 10000, helpKey: 'admin.qualityModalLivenessTimeoutHelp' },
                  { labelKey: 'admin.qualityModalQualityGateTimeoutLabel', id: 'qs-input-qualityGateTimeout', val: qs_qualityGateTimeout, set: setQsQualityGateTimeout, min: 3000, max: 300000, helpKey: 'admin.qualityModalQualityGateTimeoutHelp' },
                ] as const).map((row) => {
                  const fieldPath = row.id.replace(/^qs-input-/, '');
                  const fieldError = resolveFieldError(t, qsFieldErrors[fieldPath]);
                  return (
                    <div key={row.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 2px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: T.text, textTransform: 'uppercase', lineHeight: 1.2 }}>{t(row.labelKey)}</div>
                          <div style={{ fontSize: 8, fontWeight: 500, color: T.text, marginTop: 2, lineHeight: 1.25 }}>{t(row.helpKey)}</div>
                        </div>
                        <input type="number" data-testid={row.id} value={row.val} min={row.min} max={row.max}
                          onChange={(e) => { row.set(parseFloat(e.target.value)); clearFieldError(fieldPath); }}
                          onBlur={() => { row.set(clamp(row.val, row.min, row.max)); }}
                          style={{
                            width: row.id === 'qs-input-qualityGateTimeout' ? 80 : 64,
                            padding: '5px 6px',
                            borderRadius: 8,
                            border: `1px solid ${T.border}`,
                            background: '#FFFFFF',
                            color: T.text,
                            fontFamily: 'inherit',
                            fontSize: 11,
                            fontWeight: 600,
                            textAlign: 'center',
                            flexShrink: 0,
                            // Cross-browser spinner removal (Item 4 requirement)
                            appearance: 'textfield' as const,
                            MozAppearance: 'textfield' as const,
                            WebkitAppearance: 'textfield' as const,
                          }}
                          className="no-spinner"
                        />
                      </div>
                      <FieldError testId={`field-error-${fieldPath}`} error={fieldError} />
                    </div>
                  );
                })}
              </div>

              {/* Section — Reconocimiento facial (same row visual rules as Calidad rows) */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 800, color: T.text, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, marginTop: 12 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                  <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                  <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                  <circle cx="12" cy="11" r="3" />
                  <path d="M7 17c1-2 3-3 5-3s4 1 5 3" />
                </svg>
                {t('admin.qualityModalSectionRecognition')}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 2px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: T.text, textTransform: 'uppercase', lineHeight: 1.2 }}>{t('admin.qualityModalMatchThresholdLabel')}</div>
                    <div style={{ fontSize: 8, fontWeight: 500, color: T.text, marginTop: 2, lineHeight: 1.25 }}>{t('admin.qualityModalMatchThresholdHelp')}</div>
                  </div>
                  <input type="number" data-testid="qs-input-matchThreshold" value={qs_matchThreshold} min={0.20} max={0.90} step={0.01}
                    onChange={(e) => { setQsMatchThreshold(parseFloat(e.target.value)); clearFieldError('matchThreshold'); }}
                    onBlur={() => { setQsMatchThreshold(clamp(qs_matchThreshold, 0.20, 0.90)); }}
                    style={{
                      width: 64,
                      padding: '5px 6px',
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: '#FFFFFF',
                      color: T.text,
                      fontFamily: 'inherit',
                      fontSize: 11,
                      fontWeight: 600,
                      textAlign: 'center',
                      flexShrink: 0,
                      // Cross-browser spinner removal (Item 4 requirement)
                      appearance: 'textfield' as const,
                      MozAppearance: 'textfield' as const,
                      WebkitAppearance: 'textfield' as const,
                    }}
                    className="no-spinner"
                  />
                </div>
                <FieldError testId="field-error-matchThreshold" error={resolveFieldError(t, qsFieldErrors.matchThreshold)} />
              </div>

              {/* Consecutive Timelog Min Seconds */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 2px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: T.text, textTransform: 'uppercase', lineHeight: 1.2 }}>{t('admin.qualityModalConsecutiveTimelogLabel')}</div>
                    <div style={{ fontSize: 8, fontWeight: 500, color: T.text, marginTop: 2, lineHeight: 1.25 }}>{t('admin.qualityModalConsecutiveTimelogHelp')}</div>
                  </div>
                  <input type="number" data-testid="qs-input-consecutiveTimelogMinSeconds" value={qs_consecutiveTimelogMinSeconds} min={90} max={300} step={10}
                    onChange={(e) => { setQsConsecutiveTimelogMinSeconds(parseInt(e.target.value, 10)); clearFieldError('consecutiveTimelogMinSeconds'); }}
                    onBlur={() => { setQsConsecutiveTimelogMinSeconds(clamp(qs_consecutiveTimelogMinSeconds, 90, 300)); }}
                    style={{
                      width: 64,
                      padding: '5px 6px',
                      borderRadius: 8,
                      border: `1px solid ${T.border}`,
                      background: '#FFFFFF',
                      color: T.text,
                      fontFamily: 'inherit',
                      fontSize: 11,
                      fontWeight: 600,
                      textAlign: 'center',
                      flexShrink: 0,
                      appearance: 'textfield' as const,
                      MozAppearance: 'textfield' as const,
                      WebkitAppearance: 'textfield' as const,
                    }}
                    className="no-spinner"
                  />
                </div>
                <FieldError testId="field-error-consecutiveTimelogMinSeconds" error={resolveFieldError(t, qsFieldErrors.consecutiveTimelogMinSeconds)} />
              </div>

              {/* Section — Detección de fraude (NEW Slice 3) */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 800, color: T.text, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, marginTop: 12 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                {t('admin.qualityModalSectionFraud')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(5, auto)', gridAutoFlow: 'column', columnGap: 16, rowGap: 0 }}>
                {([
                  { labelKey: 'admin.qualityModalPassiveHighLabel', id: 'qs-input-passiveHigh', val: qs_passiveHigh, set: setQsPassiveHigh, min: 0.50, max: 1, step: 0.01, helpKey: 'admin.qualityModalPassiveHighHelp' },
                  { labelKey: 'admin.qualityModalPassiveLowLabel', id: 'qs-input-passiveLow', val: qs_passiveLow, set: setQsPassiveLow, min: 0.01, max: 1, step: 0.01, helpKey: 'admin.qualityModalPassiveLowHelp' },
                  { labelKey: 'admin.qualityModalLivenessWindowSizeLabel', id: 'qs-input-livenessWindowSize', val: qs_livenessWindowSize, set: setQsLivenessWindowSize, min: 2, max: 15, step: 1, helpKey: 'admin.qualityModalLivenessWindowSizeHelp' },
                  { labelKey: 'admin.qualityModalLivenessLiveCountLabel', id: 'qs-input-livenessLiveCount', val: qs_livenessLiveCount, set: setQsLivenessLiveCount, min: 1, max: 10, step: 1, helpKey: 'admin.qualityModalLivenessLiveCountHelp' },
                  { labelKey: 'admin.qualityModalLivenessSpoofConsecutiveLabel', id: 'qs-input-livenessSpoofConsecutive', val: qs_livenessSpoofConsecutive, set: setQsLivenessSpoofConsecutive, min: 1, max: 10, step: 1, helpKey: 'admin.qualityModalLivenessSpoofConsecutiveHelp' },
                  { labelKey: 'admin.qualityModalLivenessMaxTotalFramesLabel', id: 'qs-input-livenessMaxTotalFrames', val: qs_livenessMaxTotalFrames, set: setQsLivenessMaxTotalFrames, min: 10, max: 100, step: 1, helpKey: 'admin.qualityModalLivenessMaxTotalFramesHelp' },
                  { labelKey: 'admin.qualityModalBlurMinLaplacianVarLabel', id: 'qs-input-blurMinLaplacianVar', val: qs_blurMinLaplacianVar, set: setQsBlurMinLaplacianVar, min: 0, max: 800, step: 1, helpKey: 'admin.qualityModalBlurMinLaplacianVarHelp' },
                  { labelKey: 'admin.qualityModalBrightnessMinGateBLabel', id: 'qs-input-brightnessMinGateB', val: qs_brightnessMinGateB, set: setQsBrightnessMinGateB, min: 20, max: 180, step: 1, helpKey: 'admin.qualityModalBrightnessMinGateBHelp' },
                  { labelKey: 'admin.qualityModalBrightnessMaxGateBLabel', id: 'qs-input-brightnessMaxGateB', val: qs_brightnessMaxGateB, set: setQsBrightnessMaxGateB, min: 100, max: 254, step: 1, helpKey: 'admin.qualityModalBrightnessMaxGateBHelp' },
                  { labelKey: 'admin.qualityModalFaceMinShortSidePxLabel', id: 'qs-input-faceMinShortSidePx', val: qs_faceMinShortSidePx, set: setQsFaceMinShortSidePx, min: 40, max: 300, step: 1, helpKey: 'admin.qualityModalFaceMinShortSidePxHelp' },
                ] as const).map((row) => {
                  const fieldPath = row.id.replace(/^qs-input-/, '');
                  const fieldError = resolveFieldError(t, qsFieldErrors[fieldPath]);
                  return (
                    <div key={row.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 2px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: T.text, textTransform: 'uppercase', lineHeight: 1.2 }}>{t(row.labelKey)}</div>
                          <div style={{ fontSize: 8, fontWeight: 500, color: T.text, marginTop: 2, lineHeight: 1.25 }}>{t(row.helpKey)}</div>
                        </div>
                        <input type="number" data-testid={row.id} value={row.val} min={row.min} max={row.max} step={row.step}
                          onChange={(e) => { row.set(parseFloat(e.target.value)); clearFieldError(fieldPath); }}
                          onBlur={() => { row.set(clamp(row.val, row.min, row.max)); }}
                          style={{
                            width: 64,
                            padding: '5px 6px',
                            borderRadius: 8,
                            border: `1px solid ${T.border}`,
                            background: '#FFFFFF',
                            color: T.text,
                            fontFamily: 'inherit',
                            fontSize: 11,
                            fontWeight: 600,
                            textAlign: 'center',
                            flexShrink: 0,
                            // Cross-browser spinner removal (Item 4 requirement)
                            appearance: 'textfield' as const,
                            MozAppearance: 'textfield' as const,
                            WebkitAppearance: 'textfield' as const,
                          }}
                          className="no-spinner"
                        />
                      </div>
                      <FieldError testId={`field-error-${fieldPath}`} error={fieldError} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer — restyled to match design mock 2026-07-18:
                • Restablecer     → outline white, icon + text (flush-left)
                • Cancelar+Guardar→ centered as a pair (three-zone layout)
                     · Cancelar → outline red, text only
                     · Guardar  → solid green, check icon + text */}
            <div data-testid="qs-modal-footer" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {/* Zone 1 (flex:1) — Restablecer flush-left */}
              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <button data-testid="qs-btn-reset" onClick={() => { handleQsReset(); }}
                style={{
                  padding: '8px 16px',
                  height: 38,
                  borderRadius: 9,
                  border: `1px solid ${T.border}`,
                  background: 'transparent',
                  color: T.text,
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M3 21v-5h5" />
                </svg>
                {t('admin.qualityModalResetBtn')}
              </button>
              </div>

              {/* Zone 2 (flex:1) — Cancelar + Guardar centered as a pair */}
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 10 }}>
                <button data-testid="qs-btn-close" onClick={() => { setOpenModal(null); }}
                  style={{
                    padding: '8px 22px',
                    height: 38,
                    borderRadius: 9,
                    border: `1px solid ${C.danger}`,
                    background: 'transparent',
                    color: C.danger,
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t('admin.qualityModalCloseBtn')}
                </button>
                <button data-testid="qs-btn-save" onClick={() => { void handleQsSave(); saveParams({ debugger: debuggerVal, threshold: thresholdVal }); }} disabled={qs_saving}
                  style={{
                    padding: '8px 22px',
                    height: 38,
                    borderRadius: 9,
                    border: 'none',
                    background: C.success,
                    color: '#fff',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: qs_saving ? 'wait' : 'pointer',
                    opacity: qs_saving ? 0.7 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                  }}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  {qs_saving ? t('admin.qualityModalSavingBtn') : t('admin.qualityModalSaveBtn')}
                </button>
              </div>

              {/* Zone 3 (flex:1) — empty right spacer to keep Cancelar+Guardar
                  visually centered regardless of Restablecer's width. */}
              <div style={{ flex: 1 }} />
            </div>
          </div>
        </div>
      )}



      {/* Modal — Acerca de */}
      {openModal === 'acerca' && (
        <ModalShell title={t('admin.aboutModalTitle')} onClose={() => { setOpenModal(null); }} hideFooter>
          {ABOUT_ROWS.map((r, idx, arr) => (
            <div
              key={r.label}
              style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 2px', borderBottom: idx < arr.length - 1 ? `1px solid ${T.border}` : 'none' }}
            >
              <div style={{ color: T.textMuted, display: 'flex', flexShrink: 0 }}>{r.icon}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.text, flex: 1 }}>{r.label}</div>
              <div style={{ fontSize: r.mono ? 8 : 11, color: T.textBody, fontWeight: 500, fontFamily: r.mono ? 'ui-monospace, Menlo, monospace' : 'inherit', textAlign: 'right', wordBreak: r.mono ? 'break-all' : 'normal', maxWidth: '60%' }}>{r.val}</div>
            </div>
          ))}
          <div style={{ textAlign: 'center', fontSize: 9, color: T.textMuted, marginTop: 14 }}>Copyright © 2026 Intelli Next Corp.</div>
        </ModalShell>
      )}
    </div>
  );
}
